import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import api, { fmtNum } from "@/lib/api";
import { Scales, TrendUp, TrendDown, Wallet, Warehouse, Bank, Receipt, Coins, ArrowLineDown } from "@phosphor-icons/react";

const DIAS_OPCIONES = [7, 15, 30, 60, 90];
const MAX_DIAS_MIN = 365; // "Desde el mínimo": busca el punto más bajo del último año

const fmtFechaCorta = (iso) => {
  if (!iso) return "";
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
};

function Componente({ label, value, sign, icon: Icon, sub, testid }) {
  const neg = sign === "-";
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testid}>
      <div className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${neg ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
        <Icon size={14} weight="bold" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-[#666] font-mono">{sign} {label}</div>
        {sub && <div className="font-mono text-[10px] text-[#999] truncate">{sub}</div>}
      </div>
      <div className={`font-mono text-[13px] font-semibold shrink-0 ${neg ? "text-red-700" : "text-emerald-700"}`}>{value}</div>
    </div>
  );
}

/**
 * Posición Neta = cajas (sin cheques emitidos) + stock a costo − ctas. por pagar − pasivos.
 * Serie histórica (snapshots diarios + reconstrucción) y proyección lineal a 30 días.
 */
export default function PosicionNetaCard({ fmtMoney, cotizacion = 0 }) {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [diaSel, setDiaSel] = useState(null);
  const rate = Number(cotizacion || 0);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setDiaSel(null);
    const diasReq = dias === "min" ? MAX_DIAS_MIN : dias;
    api.get("/dashboard/patrimonio", { params: { dias: diasReq, proyeccion: 30, cotizacion: rate } })
      .then(({ data }) => {
        if (cancel) return;
        if (dias === "min" && data?.serie?.length) {
          // Recortar la serie desde el punto más bajo del histórico
          let iMin = 0;
          data.serie.forEach((r, i) => { if (r.total < data.serie[iMin].total) iMin = i; });
          const serie = data.serie.slice(iMin);
          // Regresión lineal sobre el tramo visible → proyección coherente con el rango
          const ys = serie.map((r) => r.total);
          const n = ys.length;
          const xs = ys.map((_, i) => i);
          const sx = xs.reduce((a, b) => a + b, 0), sy = ys.reduce((a, b) => a + b, 0);
          const sxx = xs.reduce((a, x) => a + x * x, 0), sxy = xs.reduce((a, x, i) => a + x * ys[i], 0);
          const den = n * sxx - sx * sx;
          const slope = n > 1 && den ? (n * sxy - sx * sy) / den : 0;
          const inter = n ? (sy - slope * sx) / n : 0;
          const proyeccion = (data.proyeccion || []).map((p, i) => ({ fecha: p.fecha, total: Math.round((inter + slope * (n - 1 + i + 1)) * 100) / 100 }));
          const hoy = ys[n - 1];
          const ult = proyeccion.length ? proyeccion[proyeccion.length - 1].total : hoy;
          data = {
            ...data,
            serie,
            proyeccion,
            fecha_minimo: data.serie[iMin].fecha,
            tendencia: {
              slope_diario: Math.round(slope * 100) / 100,
              variacion_periodo: Math.round((hoy - serie[0].total) * 100) / 100,
              total_proyectado: ult,
              variacion_proyectada: Math.round((ult - hoy) * 100) / 100,
              dias_aproximados: serie.filter((r) => r.aproximado).length,
            },
          };
        }
        setData(data); setError(null);
      })
      .catch((e) => { if (!cancel) setError(e?.response?.data?.detail || "No se pudo calcular la posición neta"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [dias, rate]);

  const chartData = useMemo(() => {
    if (!data) return [];
    const hist = (data.serie || []).map((r) => ({ fecha: r.fecha, real: r.total, aproximado: r.aproximado }));
    const last = hist[hist.length - 1];
    const proy = (data.proyeccion || []).map((r) => ({ fecha: r.fecha, proy: r.total }));
    if (last) proy.unshift({ fecha: last.fecha, proy: last.real });
    const byFecha = new Map();
    [...hist, ...proy].forEach((p) => byFecha.set(p.fecha, { ...(byFecha.get(p.fecha) || {}), ...p }));
    return Array.from(byFecha.values()).sort((a, b) => (a.fecha < b.fecha ? -1 : 1));
  }, [data]);

  const t = data?.tendencia || {};
  const c = data?.componentes || {};
  const total = Number(data?.total || 0);
  const sube = Number(t.slope_diario || 0) >= 0;
  const cajasTotal = Number(c.cajas_ars || 0) + Number(c.cajas_usd || 0) * rate;
  const ctasTotal = Number(c.ctas_pagar_ars || 0) + Number(c.ctas_pagar_usd || 0) * rate;
  const cobrarTotal = Number(c.ctas_cobrar_ars || 0) + Number(c.ctas_cobrar_usd || 0) * rate;
  const pasivosTotal = Number(c.pasivos_ars || 0) + Number(c.pasivos_usd || 0) * rate;
  const puntoSel = diaSel ? (data?.serie || []).find((r) => r.fecha === diaSel) : null;
  const hoyRow = (data?.serie || [])[(data?.serie || []).length - 1];
  const consolidar = (r, k) => Number(r?.[`${k}_ars`] || 0) + Number(r?.[`${k}_usd`] || 0) * rate;
  const FILAS_CMP = [
    { k: "cajas", l: "Cajas", sign: "+" },
    { k: "stock_costo", l: "Stock a costo", sign: "+", solo: true },
    { k: "ctas_cobrar", l: "Ctas. por cobrar", sign: "+" },
    { k: "ctas_pagar", l: "Ctas. por pagar", sign: "-" },
    { k: "pasivos", l: "Pasivos", sign: "-" },
  ];
  const valorCmp = (r, f) => (f.solo ? Number(r?.[`${f.k}_ars`] || 0) : consolidar(r, f.k));
  const sinCotiz = !rate && (Number(c.cajas_usd || 0) || Number(c.ctas_cobrar_usd || 0) || Number(c.ctas_pagar_usd || 0) || Number(c.pasivos_usd || 0));

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="posicion-neta-card">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-sm bg-[#F5F1E9] text-[#6B1F24] flex items-center justify-center">
            <Scales size={18} weight="bold" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-[15px]">Posición Neta</h2>
            <div className="text-[11px] text-[#666] font-mono">Cajas + Stock a costo + Ctas. por cobrar − Ctas. por pagar − Pasivos fin./fiscales</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[...DIAS_OPCIONES, "min"].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDias(d)}
              data-testid={`posicion-neta-dias-${d}`}
              title={d === "min" ? "Mostrar desde el punto más bajo del último año" : `Últimos ${d} días`}
              className={`px-2.5 h-7 rounded-md text-[11px] font-mono border transition inline-flex items-center gap-1 ${dias === d ? "bg-[#6B1F24] border-[#6B1F24] text-white" : "bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50"}`}
            >
              {d === "min" ? <><ArrowLineDown size={11} weight="bold" /> Desde el mínimo</> : `${d}d`}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="px-5 py-6 text-[13px] text-red-700" data-testid="posicion-neta-error">{error}</div>}

      {!error && (
        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr]">
          <div className="p-5 border-b lg:border-b-0 lg:border-r border-[#E5E5E5] space-y-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">Hoy</div>
              <div className={`font-mono text-2xl xl:text-3xl font-semibold mt-1 break-all ${total < 0 ? "text-red-700" : "text-[#1A1A1A]"}`} data-testid="posicion-neta-total">
                {loading && !data ? "…" : fmtMoney(total)}
              </div>
              <div className={`flex items-center gap-1 font-mono text-[12px] mt-1 ${sube ? "text-emerald-700" : "text-red-700"}`} data-testid="posicion-neta-variacion">
                {sube ? <TrendUp size={14} weight="bold" /> : <TrendDown size={14} weight="bold" />}
                {Number(t.variacion_periodo || 0) >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.variacion_periodo || 0))} {dias === "min" ? `desde el mínimo (${fmtFechaCorta(data?.fecha_minimo)})` : `en ${dias} días`}
              </div>
            </div>
            <div className="border border-dashed border-[#6B1F24]/40 bg-[#F5F1E9]/40 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-[#6B1F24] font-mono">Proyección a 30 días</div>
              <div className="font-mono text-xl font-semibold mt-0.5" data-testid="posicion-neta-proyectado">{fmtMoney(t.total_proyectado)}</div>
              <div className="font-mono text-[11px] text-[#666]">
                {Number(t.variacion_proyectada || 0) >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.variacion_proyectada || 0))} · ritmo {Number(t.slope_diario || 0) >= 0 ? "+" : "−"}{fmtMoney(Math.abs(t.slope_diario || 0))}/día
              </div>
            </div>
            <div className="space-y-2.5">
              <Componente label="Cajas" sign="+" icon={Wallet} value={fmtMoney(cajasTotal)} testid="posicion-neta-cajas"
                sub={`${(c.cajas_detalle || []).length} cajas · sin cheques emitidos`} />
              <Componente label="Stock a costo" sign="+" icon={Warehouse} value={fmtMoney(c.stock_costo_ars)} testid="posicion-neta-stock"
                sub={`${fmtNum(c.stock_kilos || 0, 1)} kg`} />
              <Componente label="Ctas. por cobrar" sign="+" icon={Coins} value={fmtMoney(cobrarTotal)} testid="posicion-neta-ctas-cobrar"
                sub={Number(c.ctas_cobrar_usd || 0) ? `incl. USD ${fmtNum(c.ctas_cobrar_usd, 0)}` : null} />
              <Componente label="Ctas. por pagar" sign="-" icon={Receipt} value={fmtMoney(ctasTotal)} testid="posicion-neta-ctas-pagar"
                sub={Number(c.cheques_emitidos_ars || 0) > 0 ? `incl. cheques emit. ${fmtMoney(c.cheques_emitidos_ars)}` : null} />
              <Componente label="Pasivos" sign="-" icon={Bank} value={fmtMoney(pasivosTotal)} testid="posicion-neta-pasivos" />
            </div>
            {sinCotiz ? (
              <div className="text-[10px] font-mono text-amber-700">Hay saldos en USD sin cotización cargada: no se incluyen en el total.</div>
            ) : null}
            {Number(t.dias_aproximados || 0) > 0 && (
              <div className="text-[10px] font-mono text-[#999]" data-testid="posicion-neta-aprox">
                {t.dias_aproximados} día{t.dias_aproximados !== 1 ? "s" : ""} del historial reconstruido{t.dias_aproximados !== 1 ? "s" : ""} (sin foto diaria; stock al valor actual).
              </div>
            )}
          </div>

          <div className="p-4 h-[300px]" data-testid="posicion-neta-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  const f = e?.activeLabel;
                  if (f && (data?.serie || []).some((r) => r.fecha === f)) setDiaSel((cur) => (cur === f ? null : f));
                }}
              >
                <defs>
                  <linearGradient id="pnFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6B1F24" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#6B1F24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
                <XAxis dataKey="fecha" tickFormatter={fmtFechaCorta} tick={{ fontSize: 10, fontFamily: "monospace" }} minTickGap={24} />
                <YAxis tickFormatter={(v) => `${fmtNum(v / 1_000_000, 1)}M`} tick={{ fontSize: 10, fontFamily: "monospace" }} width={52} />
                <Tooltip
                  formatter={(v, name) => [fmtMoney(v), name === "real" ? "Posición neta" : "Proyección"]}
                  labelFormatter={(l) => {
                    const p = chartData.find((x) => x.fecha === l);
                    return `${fmtFechaCorta(l)}${p?.aproximado ? " · aprox." : ""}`;
                  }}
                  contentStyle={{ fontSize: 11, fontFamily: "monospace", borderRadius: 2 }}
                />
                <ReferenceLine y={0} stroke="#B0B0B0" strokeDasharray="3 3" />
                {data?.fecha && <ReferenceLine x={data.fecha} stroke="#6B1F24" strokeDasharray="2 4" label={{ value: "Hoy", fontSize: 10, fill: "#6B1F24", position: "insideTopRight" }} />}
                {diaSel && <ReferenceLine x={diaSel} stroke="#B45309" strokeWidth={2} label={{ value: fmtFechaCorta(diaSel), fontSize: 10, fill: "#B45309", position: "insideTopLeft" }} />}
                <Area type="monotone" dataKey="real" stroke="#6B1F24" strokeWidth={2} fill="url(#pnFill)" dot={false} connectNulls={false} isAnimationActive={false} />
                <Line type="monotone" dataKey="proy" stroke={sube ? "#047857" : "#B91C1C"} strokeWidth={2} strokeDasharray="6 4" dot={false} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
            <div className="text-[10px] font-mono text-[#999] text-right -mt-1">Hacé click en un día del histórico para ver sus saldos</div>
          </div>
        </div>
      )}

      {!error && puntoSel && (
        <div className="border-t border-[#E5E5E5] px-5 py-4" data-testid="posicion-neta-comparacion">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
              Saldos al <b className="text-amber-700">{fmtFechaCorta(puntoSel.fecha)}</b>{puntoSel.aproximado ? " (aprox.)" : ""} vs. hoy
            </div>
            <button type="button" onClick={() => setDiaSel(null)} className="text-[11px] font-mono text-neutral-500 hover:text-[#6B1F24] underline decoration-dotted" data-testid="posicion-neta-cerrar-cmp">cerrar</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] font-mono">
              <thead className="text-[10px] uppercase tracking-widest text-[#666] bg-neutral-50">
                <tr>
                  <th className="px-3 py-2 text-left">Concepto</th>
                  <th className="px-3 py-2 text-right">{fmtFechaCorta(puntoSel.fecha)}</th>
                  <th className="px-3 py-2 text-right">Hoy</th>
                  <th className="px-3 py-2 text-right">Variación</th>
                </tr>
              </thead>
              <tbody>
                {FILAS_CMP.map((f) => {
                  const a = valorCmp(puntoSel, f);
                  const b = valorCmp(hoyRow, f);
                  const d = b - a;
                  const bueno = f.sign === "+" ? d >= 0 : d <= 0;
                  return (
                    <tr key={f.k} className="border-t" data-testid={`cmp-${f.k}`}>
                      <td className="px-3 py-1.5"><span className={f.sign === "+" ? "text-emerald-700" : "text-red-700"}>{f.sign}</span> {f.l}</td>
                      <td className="px-3 py-1.5 text-right">{fmtMoney(a)}</td>
                      <td className="px-3 py-1.5 text-right">{fmtMoney(b)}</td>
                      <td className={`px-3 py-1.5 text-right font-semibold ${Math.abs(d) < 0.01 ? "text-neutral-400" : bueno ? "text-emerald-700" : "text-red-700"}`}>
                        {d >= 0 ? "+" : "−"}{fmtMoney(Math.abs(d))}
                      </td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-[#6B1F24]/40 bg-[#F5F1E9]/40" data-testid="cmp-total">
                  <td className="px-3 py-2 font-semibold">= Posición neta</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(puntoSel.total)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtMoney(hoyRow?.total)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${(hoyRow?.total || 0) - puntoSel.total >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                    {(hoyRow?.total || 0) - puntoSel.total >= 0 ? "+" : "−"}{fmtMoney(Math.abs((hoyRow?.total || 0) - puntoSel.total))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
